import * as Sentry from "@sentry/react";
import { sanitizeHeaders, sanitizeObject, sanitizeUserData } from "@/utils/sentryPrivacy";
import { User } from "@/hooks/useAuthStore";

/**
 * Sentry configuration for the frontend (React)
 * Tracks web crashes and API errors from the client
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN_FRONTEND;
const ENABLE_PERFORMANCE = import.meta.env.VITE_SENTRY_ENABLE_PERFORMANCE === 'true';
const ENVIRONMENT = import.meta.env.MODE; // 'development' or 'production'

/**
 * Initialize Sentry for frontend error tracking
 */
export function initSentry() {
  // Only initialize if DSN is provided
  if (!SENTRY_DSN) {
    console.warn('Sentry DSN not configured for frontend. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    // Release version from environment variable
    release: import.meta.env.VITE_VERSION || '0.0.1',

    // Trace propagation targets for distributed tracing
    tracePropagationTargets: ["localhost", /^\//],

    // Performance monitoring (togglable via env var)
    integrations: [
      // Browser tracing for performance monitoring
      ...(ENABLE_PERFORMANCE ? [
        Sentry.browserTracingIntegration({
          // Track specific user interactions
          traceFetch: true,
          traceXHR: true,
        }),

        // Replay integration for session recording (optional, can be disabled)
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ] : []),

      // Breadcrumbs for debugging context
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),
    ],

    // Performance sampling
    tracesSampleRate: ENABLE_PERFORMANCE ? 1.0 : 0,

    // Session replay sampling (only for errors)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: ENABLE_PERFORMANCE ? 1.0 : 0,

    // Error filtering
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',

      // Random plugins/extensions
      'Can\'t find variable: ZiteReader',
      'jigsaw is not defined',
      'ComboSearch is not defined',

      // Network errors (handled by API client)
      'NetworkError',
      'Network request failed',

      // Aborted requests
      'AbortError',
      'Request aborted',
    ],

    // Data scrubbing - before sending to Sentry
    beforeSend(event, hint) {
      // Sanitize request data
      if (event.request) {
        // Scrub sensitive headers
        if (event.request.headers) {
          event.request.headers = sanitizeHeaders(event.request.headers) as { [key: string]: string };
        }

        // Sanitize request data/body
        if (event.request.data) {
          event.request.data = sanitizeObject(event.request.data) as any;
        }
      }

      // Sanitize context data
      if (event.contexts) {
        event.contexts = sanitizeObject(event.contexts) as typeof event.contexts;
      }

      // Sanitize extra data
      if (event.extra) {
        event.extra = sanitizeObject(event.extra) as typeof event.extra;
      }

      // Log the error in development
      if (ENVIRONMENT === 'development') {
        console.error('Sentry Error:', hint.originalException || hint.syntheticException);
      }

      return event;
    },

    // Breadcrumb filtering
    beforeBreadcrumb(breadcrumb) {
      // Sanitize breadcrumb data
      if (breadcrumb.data) {
        breadcrumb.data = sanitizeObject(breadcrumb.data) as typeof breadcrumb.data;
      }

      // Filter out noisy console breadcrumbs in production
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log' && ENVIRONMENT === 'production') {
        return null;
      }

      return breadcrumb;
    },
  });

  console.log(`✅ Sentry initialized (Frontend) - Environment: ${ENVIRONMENT}, Performance: ${ENABLE_PERFORMANCE ? 'enabled' : 'disabled'}`);
}

/**
 * Set the current user context in Sentry
 * Call this when user logs in or user data is available
 */
export function setSentryUser(user: User) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  // Sanitize user data before setting
  const sanitizedUser = sanitizeUserData(user);

  Sentry.setUser({
    id: user.id?.toString(),
    username: user.username,
    // Email and wallet address are already masked by sanitizeUserData
    ...sanitizedUser,
  });
}

/**
 * Clear the user context in Sentry
 * Call this when user logs out
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Manually capture an exception
 */
export function captureSentryException(error: Error, context?: Record<string, unknown>) {
  const sanitizedContext = context ? sanitizeObject(context) : undefined;

  Sentry.captureException(error, {
    extra: sanitizedContext as Record<string, unknown>,
  });
}

/**
 * Manually capture a message
 */
export function captureSentryMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, unknown>) {
  const sanitizedContext = context ? sanitizeObject(context) : undefined;

  Sentry.captureMessage(message, {
    level,
    extra: sanitizedContext as Record<string, unknown>,
  });
}

/**
 * Add a breadcrumb for debugging context
 */
export function addSentryBreadcrumb(message: string, data?: Record<string, unknown>) {
  const sanitizedData = data ? sanitizeObject(data) : undefined;

  Sentry.addBreadcrumb({
    message,
    data: sanitizedData as Record<string, unknown>,
    level: 'info',
  });
}

/**
 * Set custom tags for filtering in Sentry
 */
export function setSentryTags(tags: Record<string, string>) {
  Sentry.setTags(tags);
}

/**
 * Set custom context data
 */
export function setSentryContext(name: string, context: Record<string, unknown>) {
  const sanitizedContext = sanitizeObject(context);
  Sentry.setContext(name, sanitizedContext as Record<string, unknown>);
}
